import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetProductDetailResponse } from '@/api/handlers/resources/products/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from './productQueryKeys';

export type ProductDetailQueryOptions = {
    /**
     * The branch whose stock the variants' figures and batches count
     * (bos-app#119). `null`, the default, sends none, so the caller's own
     * scope applies — what the edit form and the purchase pickers ask for.
     */
    branchId?: string | null;
    /** `false` holds the request, e.g. while the branch is still being checked. */
    enabled?: boolean;
};

/**
 * Full product graph for the detail page (`GET /products/{id}`). Disabled until
 * a real id is present — an empty/whitespace id (e.g. a malformed route param)
 * is treated as "no product", so the request never fires for `/products/`.
 *
 * Switching branch keeps the product on screen: the previous reply for the
 * same product stands in (`isPlaceholderData`) while the new branch's loads,
 * as a refetch keeps its result (rulebook §12). A different product never
 * borrows one.
 */
export const useProductDetailQuery = (
    productId: string | null,
    { branchId = null, enabled = true }: ProductDetailQueryOptions = {},
) => {
    const { apiClient } = useApi();
    const id = productId?.trim() ? productId : null;

    return useQuery<ApiResponse<GetProductDetailResponse>>({
        queryKey: ProductQueryKeys.detail(id ?? '', branchId),
        queryFn: () =>
            apiClient.products.getProduct(
                id ?? '',
                branchId === null ? {} : { branchId },
            ),
        enabled: enabled && id !== null,
        staleTime: 60_000,
        placeholderData: (previous) =>
            previous?.success && previous.response.id === id
                ? previous
                : undefined,
    });
};
